import { Button, buttonClass, cardClass, fieldClass, SearchField, selectClass } from '@ledgerloom/ui';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Outcome } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { hasFeature, hasSurface, isAdmin, useSession } from '../session';
import { Breadcrumb, Empty, ErrorBanner, Icon, ListSkeleton, ToggleGroup, outcomeMessage, outcomeTone, useToast, ToneBadge } from '../components/ui';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field } from '../components/Field';
import { Face } from '../components/TeamFace';
import { CLASS_HELP, KIND_WORDS } from './Agents';

// Hiring an agent: every agent this workspace may hire as a gallery, found by
// describing the job, narrowed by the kind of work, the setup's picks first. A
// card opens the hire itself; a job no card does goes to the agent builder,
// which names an agent that already does it or drafts one. Patterns:
// data-display/card for the gallery, forms/search-field, content-management/modal
// for the hire.

interface Template {
  id: string; title: string; class: string; description: string; principal_types: string[]; hired: number;
  source: 'pack' | 'workspace'; version: number | null; check: { status: 'queued' | 'running' | 'passed' | 'failed'; cases: number | null; passed: number | null } | null;
}
// A playbook built in this workspace is hired only once its latest eval check passed.
const notHireable = (t: Template): string | null => t.source !== 'workspace' ? null
  : !t.check ? 'Needs a passing eval check' : t.check.status === 'passed' ? null : t.check.status === 'failed' ? `Its eval check failed (${t.check.passed ?? 0} of ${t.check.cases ?? 0})` : 'Its eval check is still running';

type Kind = 'all' | 'automate' | 'assist' | 'advise' | 'configure';

// Words a person types that say nothing about the job.
const FILLER = new Set(['the', 'and', 'for', 'that', 'with', 'our', 'your', 'want', 'need', 'help', 'someone', 'something', 'agent', 'agents', 'who', 'can', 'will', 'would', 'like', 'all', 'any', 'from', 'into', 'when', 'what', 'does', 'this', 'them', 'they', 'are']);
const words = (s: string) => s.toLowerCase().match(/[a-z0-9]+/g) ?? [];
// The same word in another form (reply and replies, lead and leads): they share all but the last two letters of the shorter, and at least four.
function same(a: string, b: string) {
  let n = 0;
  while (n < a.length && a[n] === b[n]) n++;
  return a === b || n >= Math.max(4, Math.min(a.length, b.length) - 2);
}
// How well an agent answers what the person typed: each word of theirs its title (worth three) or description holds.
function matchScore(t: Pick<Template, 'title' | 'description'>, asked: string): number {
  const title = words(t.title), text = words(t.description);
  return words(asked).filter((w) => w.length > 2 && !FILLER.has(w))
    .reduce((s, w) => s + (title.some((x) => same(x, w)) ? 3 : 0) + (text.some((x) => same(x, w)) ? 1 : 0), 0);
}

export function AgentHire() {
  const { me } = useSession();
  usePageTitle('Hire an agent');
  const canHire = isAdmin(me) || me.role === 'manager';
  const builder = canHire && hasFeature(me, 'agent_builder') && hasSurface(me, 'agent_builder');
  const templates = useApi<Template[]>('/v1/agent-templates');
  // The agents the workspace setup recommended, with why.
  const setup = useApi<{ setup?: { payload: { recommended_agents?: Array<{ template_id: string; why: string }> } } | null }>('/v1/onboarding');
  const why = new Map((setup.data?.setup?.payload.recommended_agents ?? []).map((r) => [r.template_id, r.why]));
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<Kind>('all');
  const choosing = templates.data?.find((t) => t.id === params.get('template'));
  const choose = (id: string | null) => { const next = new URLSearchParams(params); if (id) next.set('template', id); else next.delete('template'); setParams(next, { replace: true }); };

  const all = templates.data ?? [];
  const kinds = (Object.keys(KIND_WORDS) as Array<Exclude<Kind, 'all'>>).filter((k) => all.some((t) => t.class === k));
  const searching = q.trim().length > 0;
  const list = all.filter((t) => kind === 'all' || t.class === kind).map((t) => ({ t, score: matchScore(t, q) }));
  // Searching, the best match leads; browsing, the setup's picks, then the agents not on the team yet.
  const shown = searching
    ? list.filter((x) => x.score > 0).sort((a, b) => b.score - a.score).map((x) => x.t)
    : list.map((x) => x.t).sort((a, b) => Number(why.has(b.id)) - Number(why.has(a.id)) || Number(a.hired > 0) - Number(b.hired > 0));

  return (
    <div className="stack hire">
      <Breadcrumb items={[{ to: '/agents', label: 'Agents' }, { label: 'Hire an agent' }]} />
      <header className="hire-hero">
        <h1>Hire an agent</h1>
        <p>Say what you need done. Every agent starts on trial: its work is checked and logged, never saved or sent, until someone makes it active.</p>
        <div className="hire-search">
          <SearchField id="hire-q" label="Describe the job" autoComplete="off" value={q} onValueChange={setQ} placeholder="Describe the job, like answer new leads within minutes" className="h-14 rounded-pill bg-surface text-base shadow-raised" />
        </div>
        {kinds.length > 1 && <ToggleGroup label="Kind of work" value={kind} options={[['all', 'All'], ...kinds.map((k) => [k, KIND_WORDS[k]] as [Kind, string])]} onChange={setKind} />}
      </header>

      <ErrorBanner error={templates.error} />
      <section aria-labelledby="hire-gallery-h" aria-busy={!templates.data} className="stack" style={{ gap: 16 }}>
        <h2 id="hire-gallery-h" className="hire-h2">{searching ? (shown.length ? `Best matches for “${q.trim()}”` : `Nothing here does “${q.trim()}” yet`) : kind === 'all' ? 'Every agent you can hire' : KIND_WORDS[kind]}</h2>
        {!templates.data ? (templates.error ? null : <div className={cardClass}><ListSkeleton rows={4} label="Loading agents to hire" /></div>) : !shown.length ? (
          <div className={cardClass}>
            <Empty title={searching ? 'No agent here matches that' : 'No agents of this kind'} action={builder && searching ? <Link className={buttonClass()} to={`/agents/build?request=${encodeURIComponent(q.trim())}`}><Icon name="agent" />Ask the agent builder</Link> : undefined}>
              {builder && searching ? 'The agent builder reads what you need, names an agent that already does it, or drafts a new one for an admin to sign.' : 'Try other words, or show every kind of work.'}
            </Empty>
          </div>
        ) : (
          <ul className="hire-grid" aria-label="Agents you can hire">
            {shown.map((t) => {
              const blocked = notHireable(t);
              return (
                <li key={t.id} className={`${cardClass} hire-card`}>
                  <Face kind="agent" name={t.title} template={t.id} size={56} />
                  <div className="stack" style={{ gap: 4 }}>
                    <h3>{t.title}</h3>
                    <span className="faint small">{KIND_WORDS[t.class] ?? 'Agent'}{t.source === 'workspace' ? ' · Built here' : ''}</span>
                  </div>
                  <p>{t.description}</p>
                  {why.has(t.id) && <p className="hire-why"><strong>Recommended.</strong> {why.get(t.id)}</p>}
                  <div className="hire-card-foot">
                    {t.hired > 0 ? <ToneBadge tone="work">{t.hired === 1 ? 'On your team' : `${t.hired} on your team`}</ToneBadge> : <span />}
                    {canHire && (blocked ? <span className="faint small">{blocked}</span>
                      : <Button type="button" size="sm" aria-label={`Hire ${t.title}`} onClick={() => choose(t.id)}>{t.hired ? 'Hire another' : 'Hire'}</Button>)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {builder && !!shown.length && (
          <div className={`${cardClass} hire-build`}>
            <Face kind="agent" name="Agent builder" template="agent-builder" size={44} />
            <div className="grow"><strong>Not quite the job?</strong><div className="faint">Describe it in your own words, and the agent builder finds the agent for it or drafts one.</div></div>
            <Link className={buttonClass({ variant: 'secondary' })} to={`/agents/build${searching ? `?request=${encodeURIComponent(q.trim())}` : ''}`}><Icon name="agent" />Ask the agent builder</Link>
          </div>
        )}
        {!canHire && <p className="faint">An admin or a manager hires agents. Ask one of them for the agent you need.</p>}
      </section>
      {choosing && canHire && !notHireable(choosing) && <HireDialog tpl={choosing} onClose={() => choose(null)} />}
    </div>
  );
}

function HireDialog({ tpl, onClose }: { tpl: Template; onClose: () => void }) {
  const { me, refreshStats, refreshMe } = useSession();
  const users = useApi<Array<{ id: string; display_name: string; status: string }>>('/v1/users');
  const teams = useApi<Array<{ id: string; name: string; status: string }>>('/v1/teams');
  // 'tenant', 'team:<id>', or 'user:<id>'; empty means the playbook's default.
  const [principal, setPrincipal] = useState('');
  // Who hears about its problems (failed runs, budget caps): the hirer unless they pick someone else.
  const [owner, setOwner] = useState(me.user_id);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const can = (type: string) => tpl.principal_types.includes(type);
  const activeTeams = teams.data?.filter((t) => t.status === 'active') ?? [];
  const people = users.data?.filter((u) => u.status === 'active') ?? [];
  const chosen = principal || (can('tenant') ? 'tenant' : `user:${me.user_id}`);
  const choices = Number(can('tenant')) + Number(can('team') && activeTeams.length > 0) + Number(can('user'));
  async function hire() {
    setBusy(true); setError(null);
    try {
      const [type, id] = chosen.split(':');
      const out = await api<Outcome>('/v1/agents/hire', { body: { template_id: tpl.id, principal_type: type, ...(id ? { principal_id: id } : {}), owner_user_id: owner, ...(label.trim() ? { name: label.trim() } : {}), reason: `Hire ${tpl.title}` } });
      toast(outcomeMessage(out, 'Hired in shadow'), outcomeTone(out));
      refreshStats(); refreshMe();
      navigate(out.state === 'applied' && out.result?.agent_id ? `/agents/${encodeURIComponent(out.result.agent_id)}` : '/agents');
    } catch (e) { setError(e); setBusy(false); }
  }
  return (
    <ConfirmDialog open title={`Hire ${tpl.title}`} confirmLabel="Propose hire" busy={busy} onConfirm={hire} onCancel={onClose}>
      <div className="hire-sheet-top">
        <Face kind="agent" name={tpl.title} template={tpl.id} size={56} />
        <div className="stack" style={{ gap: 4 }}>
          <span>{tpl.description}</span>
          <span className="faint small">{CLASS_HELP[tpl.class]}</span>
        </div>
      </div>
      <ErrorBanner error={error} />
      {(choices > 1 || !can('tenant')) && (
        <Field label="Works for" hint={choices > 1 ? 'The company, a team, or one person. For a person, it sees what they see, drafts in their name, and they approve each draft.' : 'This agent works for one person: it sees what they see, drafts in their name, and they approve each draft.'}>
          <select className={selectClass} value={chosen} onChange={(e) => setPrincipal(e.target.value)}>
            {can('tenant') && <option value="tenant">The company</option>}
            {can('team') && activeTeams.length > 0 && (
              <optgroup label="Teams">{activeTeams.map((t) => <option key={t.id} value={`team:${t.id}`}>{t.name}</option>)}</optgroup>
            )}
            {can('user') && (
              <optgroup label="People">{people.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.display_name}{u.id === me.user_id ? ' (you)' : ''}</option>)}</optgroup>
            )}
          </select>
        </Field>
      )}
      <Field label="Owner" hint="Who hears about its problems: failed runs, budget caps, a question nobody else can answer.">
        <select className={selectClass} value={owner} onChange={(e) => setOwner(e.target.value)}>
          {people.map((u) => <option key={u.id} value={u.id}>{u.display_name}{u.id === me.user_id ? ' (you)' : ''}</option>)}
        </select>
      </Field>
      <Field label="Label" hint={`How it is named on the team. Leave it empty for "${tpl.title}", numbered if you already have one.`}>
        <input className={fieldClass} value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} placeholder={tpl.title} />
      </Field>
      <p className="muted small" style={{ margin: 0 }}>Hiring is a change set: another admin signs it, unless the workspace's sign-off policy lets you hire alone. It starts on trial.</p>
    </ConfirmDialog>
  );
}
